import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { MAINTENANCE, LEFT_FACTORY, daysSince, fmtDateTime } from "@/lib/status";
import PageHeader from "@/components/PageHeader";
import StatusBadge, { PriorityBadge, TypeBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Wrench, Truck } from "lucide-react";
import { toast } from "sonner";

const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

function PickupDialog({ rework, onClose, onDone }) {
  const [by, setBy] = useState("");
  const [at, setAt] = useState(toLocalInput(new Date()));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/reworks/${rework.id}/left-factory`, {
        picked_up_by: by, picked_up_at: new Date(at).toISOString(), note,
      });
      toast.success(`${rework.job_number} / ${rework.part_number} has left the factory`);
      onDone(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not record pickup");
    } finally {
      setBusy(false);
    }
  };

  const field = "rounded-sm border-[#cfcfc9] bg-white text-[#232826] focus-visible:ring-[#647265]";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-[#e6e5df] rounded-sm" data-testid="pickup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#232826]">Mark as Left Factory</DialogTitle>
          <DialogDescription className="text-[#6b6a68]">
            <span className="mono font-semibold text-[#232826]">{rework.job_number} / {rework.part_number}</span> — record who collected it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-widest text-[#6b6a68]" htmlFor="pickup-by">Picked up by (installer)</label>
            <Input id="pickup-by" data-testid="pickup-by" required autoFocus value={by} onChange={(e) => setBy(e.target.value)} placeholder="e.g. Dave — ABC Installs" className={field} />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-widest text-[#6b6a68]" htmlFor="pickup-at">Pickup date & time</label>
            <Input id="pickup-at" data-testid="pickup-at" type="datetime-local" required value={at} onChange={(e) => setAt(e.target.value)} className={field} />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-widest text-[#6b6a68]" htmlFor="pickup-note">Note (optional)</label>
            <Textarea id="pickup-note" data-testid="pickup-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Vehicle, site, anything useful" className={field} />
          </div>
          <Button type="submit" disabled={busy || !by.trim()} data-testid="pickup-submit"
            className="w-full h-11 rounded-sm bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] text-xs uppercase tracking-widest font-semibold">
            <Truck size={14} className="mr-2" /> {busy ? "Saving…" : "Confirm left factory"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Maintenance() {
  const { user } = useAuth();
  const canRelease = !["kiosk", "management"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [picking, setPicking] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reworks");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  }, []);
  useAutoRefresh(load);

  const waiting = rows.filter((r) => r.status === MAINTENANCE);
  const left = rows.filter((r) => r.status === LEFT_FACTORY && daysSince(r.pickup?.at || r.date_completed) <= 7);
  const byJob = (list) => Object.values(list.reduce((acc, r) => { (acc[r.job_number] ||= { job: r.job_number, items: [] }).items.push(r); return acc; }, {}));
  const replace = (d) => setRows((rs) => rs.map((x) => (x.id === d.id ? d : x)));

  return (
    <div>
      <PageHeader title="Maintenance Clearing" subtitle="Parts waiting for an installer. Mark them as left factory when collected." testid="maintenance-header" />
      <div className="p-6 md:p-10 grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section className="border border-[#e6e5df] bg-white rounded-sm" data-testid="maintenance-waiting">
          <div className="px-5 py-4 border-b border-[#e6e5df] flex items-center gap-3">
            <Wrench size={16} className="text-amber-600" />
            <div className="font-display font-bold text-[#232826]">Awaiting pickup</div>
            <span className="ml-auto num text-sm text-[#424e43]" data-testid="maintenance-waiting-count">{waiting.length}</span>
          </div>
          {waiting.length === 0 && <div className="p-8 text-sm text-[#a39f98] text-center">Nothing waiting in Maintenance Clearing.</div>}
          {byJob(waiting).map((g) => (
            <div key={g.job}>
              <div className="px-4 py-2 bg-[#efece5] flex items-center justify-between">
                <span className="mono text-sm font-semibold text-[#232826]">{g.job}</span>
                <span className="text-[10px] uppercase tracking-widest text-[#6b6a68]">{g.items.length} part{g.items.length !== 1 ? "s" : ""}</span>
              </div>
              {g.items.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-[#e6e5df] last:border-0" data-testid={`maintenance-row-${r.id}`}>
                  <div className="min-w-0 flex-1 basis-40">
                    <Link to={`/rework/${r.id}`} className="mono text-sm text-[#232826] font-medium hover:text-[#647265]">{r.part_number}</Link>
                    <div className="text-xs text-[#6b6a68] truncate">{r.rework_reason} · {r.description}</div>
                  </div>
                  <PriorityBadge priority={r.priority} />
                  <TypeBadge type={r.request_type} />
                  <div className="w-20 text-right text-[11px] font-mono text-[#a39f98]">
                    {r.bay_number ? <span data-testid={`maintenance-bay-label-${r.id}`}>Bay {r.bay_number}</span> : "No bay"}
                    <div>{daysSince(r.status_changed_at)}d waiting</div>
                  </div>
                  {canRelease && (
                    <Button onClick={() => setPicking(r)} data-testid={`left-factory-${r.id}`}
                      className="h-9 rounded-sm bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] text-[11px] uppercase tracking-widest px-3">
                      <Truck size={12} className="mr-1.5" /> Left factory
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ))}
        </section>

        <section className="border border-[#e6e5df] bg-white rounded-sm" data-testid="maintenance-left">
          <div className="px-5 py-4 border-b border-[#e6e5df] flex items-center gap-3">
            <Truck size={16} className="text-slate-600" />
            <div className="font-display font-bold text-[#232826]">Left factory — last 7 days</div>
            <span className="ml-auto num text-sm text-[#424e43]">{left.length}</span>
          </div>
          {left.length === 0 && <div className="p-8 text-sm text-[#a39f98] text-center">No pickups recorded this week.</div>}
          {byJob(left).map((g) => (
            <div key={g.job}>
              <div className="px-4 py-2 bg-[#efece5] flex items-center justify-between">
                <span className="mono text-sm font-semibold text-[#232826]">{g.job}</span>
                <StatusBadge status={LEFT_FACTORY} />
              </div>
              {g.items.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-[#e6e5df] last:border-0" data-testid={`left-row-${r.id}`}>
                  <div className="min-w-0 flex-1 basis-40">
                    <Link to={`/rework/${r.id}`} className="mono text-sm text-[#232826] font-medium hover:text-[#647265]">{r.part_number}</Link>
                    <div className="text-xs text-[#6b6a68] truncate">{r.rework_reason} · {r.description}</div>
                  </div>
                  <div className="text-right text-xs text-[#6b6a68]" data-testid={`left-pickup-${r.id}`}>
                    <div className="font-medium text-[#232826]">{r.pickup?.by}</div>
                    <div className="font-mono text-[11px] text-[#a39f98]">{fmtDateTime(r.pickup?.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </section>
      </div>
      {picking && <PickupDialog rework={picking} onClose={() => setPicking(null)} onDone={(d) => { replace(d); setPicking(null); }} />}
    </div>
  );
}
